import React, { useCallback, useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ReplyPostContent } from '@components/modals/ReplyModal/Content';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  FeedPostCommentModalFragmentDoc,
  type FragmentType,
  getFragment,
  PostCommentDocument,
  useMutation,
} from '@graphql/reactQuery';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPostCommentModal on Post {
    id
    content
    webappUri
    __typename
    creator {
      id
      username
      avatar {
        id
        url
      }
      webappUri
      publicAddress
      displayName
      artist {
        id
        name
      }
    }
    ... on ActivityFeedGroupBackingPost {
      activityFeedGroup {
        information {
          __typename
          ... on ReleaseDroppedAggregate {
            release {
              id
              title
            }
          }
        }
      }
    }
    ... on ArtistDropPost {
      release {
        id
        title
      }
    }
  }
`);

export function FeedPostReplyModal({
  postFrag,
}: {
  postFrag: FragmentType<FeedPostCommentModalFragmentDoc>;
}) {
  const { closeCancelModal } = useModal();
  const { openToast } = useToast();
  const { mutateAsync, isLoading } = useMutation(PostCommentDocument, {});

  const closeModal = useStableCallback(() => {
    closeCancelModal();
  });

  const post = getFragment(FeedPostCommentModalFragmentDoc, postFrag);

  const handleSubmitClicked = useCallback(
    async (content: string) => {
      if (content.length) {
        try {
          const response = await mutateAsync({
            input: {
              postId: post.id,
              content,
            },
          });
          if (response.data.addPostComment.__typename === 'MutationAddPostCommentSuccess') {
            openToast({
              text: 'Reply added',
              variant: 'success',
              ctaText: 'View',
              ctaHref: webappUriPath(post.webappUri),
            });
            closeModal();
          } else if (response.data.addPostComment.__typename === 'ValidationError') {
            openToast({ text: response.data.addPostComment.message, variant: 'error' });
          } else {
            openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
          }
        } catch (error) {
          openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
        }
      }
    },
    [post.id, mutateAsync, openToast, closeModal, post.webappUri],
  );

  const postContent = useMemo(() => {
    switch (post.__typename) {
      case 'ActivityFeedGroupBackingPost':
        if (post.activityFeedGroup.information.__typename === 'ReleaseDroppedAggregate') {
          return `Dropped \"${post.activityFeedGroup.information.release.title}\"`;
        } else {
          return post.content;
        }

      default:
        return post.content;
    }
  }, [post]);

  return (
    <StandardModal
      title="Reply"
      closeModal={closeModal}
      bodyWithoutPaddingBottom
      body={
        <ReplyPostContent
          isSubmitting={isLoading}
          postContent={postContent}
          postCreator={post.creator}
          isLoading={false}
          onSubmit={handleSubmitClicked}
        />
      }
    />
  );
}
